/**
 * TaskBoardPage: Task board for Seitiate+ at /board.
 *
 * Three views: Kanban (drag-and-drop, primary), Stream (room-grouped), Timeline.
 * Ported from context-engine-studio with full visual fidelity.
 * Uses the engine task API for data.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  Filter,
  RefreshCw,
  Plus,
  Columns3,
  List,
  Clock,
  X,
  Sun,
  Moon,
  Loader2,
  AlertCircle,
  Bell,
  BellOff,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import api from '@/api/engine';
import type { TaskItem, TaskSummary, TaskStatus, TaskPriority } from '@/api/types';
import { KanbanView } from '../components/board/KanbanView';
import { BoardDetailPanel } from '../components/board/BoardDetailPanel';
import { BoardShareModal } from '../components/board/BoardShareModal';
import { useBoardEvents } from '../components/board/useBoardEvents';
import { NewTaskModal } from '../components/dashboard/NewTaskModal';
import { MilestoneTimeline } from '../components/dashboard/MilestoneTimeline';
import { TaskRow } from '../components/dashboard/TaskRow';
import { useTheme, type ThemeName } from '@/lib/theme';
import { useAuth } from '@/stores/auth.hooks';
import { countUndatedActive, formatTaskDate, timelineDatedEmptyMessage } from '@/lib/taskDates';
import { groupOwners, ownerFilterPredicate, type OwnerFilterValue } from '@/lib/taskOwner';
import { countNeedsMe, isNeedsMe, type NeedsMeContext } from '@/lib/needsMe';
import { useNeedsMeNotifications } from '../components/board/useNeedsMeNotifications';

// ── Types ───────────────────────────────────────────────

type BoardView = 'kanban' | 'stream' | 'timeline';

type RoomKey = 'decisions' | 'sakme' | 'seitiate' | 'closedclaw' | 'skills' | 'launch' | 'solopreneurs' | 'cross' | 'archive' | 'other';

// ── Room routing (mirrors TasksTab) ─────────────────────

const CROSS_PRODUCT_CATEGORIES: ReadonlySet<string> = new Set([
  'director-personal', 'strategy', 'ip', 'research', 'security',
  'trust', 'deploy-regression', 'ux-hygiene', 'coordination',
]);

const HISTORICAL_PATTERNS: RegExp[] = [
  /^pipeline-.*-(mar|feb|jan)-/i, /^wave-/i, /^level-/i, /^archived-/i,
  /^deferred-/i, /^phase-2-/i, /^phase-3-/i, /-complete$/i,
];

const LEGACY_ALIASES: Record<string, RoomKey> = {
  'launch-v1': 'launch', 'launch-v2': 'launch', 'launch-prep-ship': 'launch',
  'brainstorm-rulings': 'cross', 'admin-reconcile': 'cross',
};

interface RoomDef {
  key: RoomKey;
  label: string;
  color: string;
}

const ROOMS: RoomDef[] = [
  { key: 'decisions',  label: 'DECISIONS',      color: '#c084fc' },
  { key: 'sakme',      label: 'SAKME',          color: '#a78bfa' },
  { key: 'seitiate',   label: 'SEITIATE',       color: '#34d399' },
  { key: 'closedclaw', label: 'CLOSEDCLAW',     color: '#f87171' },
  { key: 'skills',     label: 'SKILLS',         color: '#fbbf24' },
  { key: 'launch',     label: 'LAUNCH',         color: '#22d3ee' },
  { key: 'solopreneurs', label: 'SOLOPRENEURS', color: '#60a5fa' },
  { key: 'cross',      label: 'CROSS-PRODUCT',  color: '#94a3b8' },
];

function categoryToRoom(category: string): RoomKey {
  const alias = LEGACY_ALIASES[category];
  if (alias) return alias;
  if (category.startsWith('sakme/')) return 'sakme';
  if (category.startsWith('seitiate/')) return 'seitiate';
  if (category.startsWith('closedclaw/')) return 'closedclaw';
  if (category.startsWith('skills/')) return 'skills';
  if (category.startsWith('launch/')) return 'launch';
  if (category.startsWith('solopreneurs/')) return 'solopreneurs';
  if (CROSS_PRODUCT_CATEGORIES.has(category)) return 'cross';
  if (HISTORICAL_PATTERNS.some((re) => re.test(category))) return 'archive';
  if (
    category === 'strategy' ||
    (category.startsWith('RULING-'))
  ) return 'decisions';
  return 'other';
}

// Tags-first room routing — the doctrine (docs/coordination/TASK-CATEGORY-
// TAXONOMY.md lines 25/27/118/199) says tasks with `decision-required` in
// tags surface in the derived DECISIONS room, regardless of category. This
// must run BEFORE categoryToRoom because the strategy category falls under
// CROSS_PRODUCT_CATEGORIES first, which would mis-route RULING-* tasks to
// the 'cross' room. Bug A of BOARD-DECISIONS-VIEW-AND-RULING-DISPATCH-SHAPE-1.
function taskToRoom(task: { category?: string | null; tags?: string[] | null }): RoomKey {
  if (Array.isArray(task.tags) && task.tags.includes('decision-required')) {
    return 'decisions';
  }
  return categoryToRoom(task.category || 'Uncategorized');
}


// ── Sort helpers ────────────────────────────────────────

const PRIORITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
const STATUS_RANK: Record<string, number> = {
  in_progress: 0, review: 1, blocked: 2, todo: 3, backlog: 4, done: 5, cancelled: 6,
};

function compareTasksForBoard(a: TaskItem, b: TaskItem): number {
  const ta12 = Array.isArray(a.tags) && a.tags.includes('top-12');
  const tb12 = Array.isArray(b.tags) && b.tags.includes('top-12');
  if (ta12 !== tb12) return ta12 ? -1 : 1;
  const sa = STATUS_RANK[a.status] ?? 99;
  const sb = STATUS_RANK[b.status] ?? 99;
  if (sa !== sb) return sa - sb;
  const pa = PRIORITY_RANK[a.priority] ?? 99;
  const pb = PRIORITY_RANK[b.priority] ?? 99;
  if (pa !== pb) return pa - pb;
  const taTime = a.created_at ? new Date(a.created_at).getTime() : 0;
  const tbTime = b.created_at ? new Date(b.created_at).getTime() : 0;
  return taTime - tbTime;
}

// ── Local storage keys ──────────────────────────────────
const BOARD_VIEW_KEY = 'sp.board.view';
const BOARD_ROOM_KEY = 'sp.board.room_filter';
const BOARD_OWNER_KEY = 'sp.board.owner_filter';

// =============================================================================
// TaskBoardPage Component
// =============================================================================

export function TaskBoardPage() {
  // ── Theme (via ThemeProvider) ────────────────────────
  const { themeName, setTheme } = useTheme();
  const isDark = themeName !== 'light';

  // ── Auth + workspace ────────────────────────────────
  const { user, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read-only: viewer role or ?view=readonly query param
  const forceReadOnly = searchParams.get('view') === 'readonly';

  // "What Needs Me" filter (WHAT-NEEDS-ME-VIEW-1). Activated by ?filter=needs-me
  // OR by clicking the badge in the header. Persisted via URL only — this is a
  // navigational state, not a saved preference, so cmd-click on the badge in
  // a new tab opens that tab in the filtered state.
  const needsMeOnly = searchParams.get('filter') === 'needs-me';
  const boardWorkspace = user?.workspaces?.find((w) => w.thread_type === 'task_board');
  const userRole: string | null = boardWorkspace
    ? (boardWorkspace.participant_role ?? 'member')
    : user?.is_director ? 'owner' : null;
  const readOnly = forceReadOnly || userRole === 'viewer';
  const hasExplicitAccess = Boolean(userRole) || Boolean(user?.is_director);

  // ── Data state ──────────────────────────────────────
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [summary, setSummary] = useState<TaskSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // A successful task fetch is positive proof of access — the engine
  // backend only returns task data to callers it accepts, so a fetch
  // that resolved without error means the user is authorized regardless
  // of how many tasks came back. An authorized empty board is still
  // access; "no tasks" is not the same as "no access."
  // This avoids painting "You don't have access to this board" over a
  // board the user is allowed to read (e.g. when /auth/me hasn't surfaced
  // the is_director claim yet because CES_TEAM_USERS is unset, but the
  // legacy admin_email→DIRECTOR path still authorizes the read).
  const tasksConfirmedAccess = !loading && !error;
  const noAccess = !hasExplicitAccess && !tasksConfirmedAccess;

  // ── UI state ────────────────────────────────────────
  const [view, setView] = useState<BoardView>(() => {
    const saved = localStorage.getItem(BOARD_VIEW_KEY);
    return (saved === 'kanban' || saved === 'stream' || saved === 'timeline') ? saved : 'kanban';
  });
  const [selectedTask, setSelectedTask] = useState<TaskItem | null>(null);
  const [showNewTask, setShowNewTask] = useState(false);
  const [newTaskCreating, setNewTaskCreating] = useState(false);
  const [newTaskError, setNewTaskError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // ── Filters ─────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRoom, setFilterRoom] = useState<string>(() => {
    return localStorage.getItem(BOARD_ROOM_KEY) ?? 'all';
  });
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  // Owner filter: 'all' | 'unassigned' | 'humans' | 'agents' | '@me' | <handle>
  // Persisted across reloads (small team — viewers care about *their* slice).
  const [filterOwner, setFilterOwner] = useState<OwnerFilterValue>(() => {
    const saved = localStorage.getItem(BOARD_OWNER_KEY);
    return (saved && saved.length > 0) ? saved : 'all';
  });

  // ── Room collapse state (stream view) ───────────────
  const [roomCollapsed, setRoomCollapsed] = useState<Record<string, boolean>>({});

  // ── Persist preferences ─────────────────────────────
  useEffect(() => { localStorage.setItem(BOARD_VIEW_KEY, view); }, [view]);
  useEffect(() => { localStorage.setItem(BOARD_ROOM_KEY, filterRoom); }, [filterRoom]);
  useEffect(() => { localStorage.setItem(BOARD_OWNER_KEY, filterOwner); }, [filterOwner]);

  // ── Toast auto-dismiss ──────────────────────────────
  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(null), 3500);
    return () => clearTimeout(t);
  }, [toastMessage]);

  // ── Fetch data ──────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, taskList] = await Promise.all([
        api.tasks.summary(),
        api.tasks.listAll(),
      ]);
      setSummary(summaryData);
      setTasks(taskList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── SSE + polling for real-time updates ──────────────
  const silentRefresh = useCallback(() => {
    api.tasks.listAll()
      .then((updated) => setTasks(updated))
      .catch(() => { /* silent — polling is best-effort */ });
  }, []);

  useBoardEvents({ tasks, onRefresh: silentRefresh });

  // ── Task mutations ──────────────────────────────────
  const replaceTask = useCallback((updated: TaskItem) => {
    setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
  }, []);

  const handleStatusChange = useCallback(async (taskId: string, newStatus: TaskStatus): Promise<TaskItem> => {
    if (newStatus === 'done') {
      const updated = await api.tasks.complete(taskId);
      replaceTask(updated);
      return updated;
    }
    const updated = await api.tasks.update(taskId, { status: newStatus } as Partial<TaskItem>);
    replaceTask(updated);
    return updated;
  }, [replaceTask]);

  const handleActivate = useCallback(async (task: TaskItem) => {
    try {
      const updated = await api.tasks.update(task.id, { status: 'in_progress' } as Partial<TaskItem>);
      replaceTask(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Activate failed');
    }
  }, [replaceTask]);

  const handleComplete = useCallback(async (task: TaskItem) => {
    try {
      const updated = await api.tasks.complete(task.id);
      replaceTask(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Complete failed');
    }
  }, [replaceTask]);

  const handleOpenPrompt = useCallback(async (task: TaskItem) => {
    setSelectedTask(task);
  }, []);

  const handleCreateTask = useCallback(async (payload: {
    task_id: string; title: string; description: string;
    category: string; priority: TaskPriority; tier: number; tags: string[];
  }) => {
    setNewTaskCreating(true);
    setNewTaskError(null);
    try {
      await api.tasks.create({ ...payload, status: 'todo' });
      await fetchData();
      setShowNewTask(false);
      setToastMessage('Task created');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create task';
      if (/already exists|duplicate|UNIQUE|409|400/i.test(msg)) {
        setNewTaskError(`task_id "${payload.task_id}" already exists`);
      } else {
        setNewTaskError(msg);
      }
    } finally {
      setNewTaskCreating(false);
    }
  }, [fetchData]);

  // ── Derived data ────────────────────────────────────
  const needsMeContext: NeedsMeContext = useMemo(() => ({
    meHandle: user?.display_name ?? null,
    meIsOwner: Boolean(user?.is_director) || userRole === 'owner',
  }), [user?.display_name, user?.is_director, userRole]);

  const filteredTasks = useMemo(() => {
    let result = tasks;
    // Default (filterStatus='all'): hide cancelled AND done — Director ruling
    // 2026-05-12. The board is for active work; archived items reachable via
    // explicit "all_including_archived" option or by picking the specific
    // Done/Cancelled status from the dropdown.
    if (filterStatus === 'all') {
      result = result.filter((t) => t.status !== 'cancelled' && t.status !== 'done');
    } else if (filterStatus === 'all_including_archived') {
      // Show everything — pass through. Later filters still apply.
    } else if (filterStatus !== 'cancelled' && filterStatus !== 'done') {
      // For any other specific status (todo, in_progress, etc.), the existing
      // exact-match filter below handles it. Still hide cancelled here for
      // safety because the later filter doesn't run for, e.g., 'todo'.
      result = result.filter((t) => t.status !== 'cancelled');
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((t) =>
        t.title.toLowerCase().includes(q) ||
        (t.task_id && t.task_id.toLowerCase().includes(q)) ||
        (t.description && t.description.toLowerCase().includes(q))
      );
    }
    if (filterRoom !== 'all') {
      result = result.filter((t) => taskToRoom(t) === filterRoom);
    }
    if (filterStatus !== 'all' && filterStatus !== 'all_including_archived') {
      result = result.filter((t) => t.status === filterStatus);
    }
    if (filterPriority !== 'all') {
      result = result.filter((t) => t.priority === filterPriority);
    }
    if (filterOwner !== 'all') {
      const predicate = ownerFilterPredicate(filterOwner, {
        meHandle: user?.display_name ?? null,
        meUserId: user?.uid ?? null,
      });
      result = result.filter(predicate);
    }
    if (needsMeOnly) {
      result = result.filter((t) => isNeedsMe(t, needsMeContext));
    }
    return result;
  }, [tasks, searchQuery, filterRoom, filterStatus, filterPriority, filterOwner, needsMeOnly, needsMeContext, user?.display_name, user?.uid]);

  // "What Needs Me" badge count — computed from the unfiltered task list so
  // the badge stays accurate regardless of which other filters are active.
  // Updates whenever a task transitions into or out of `workflow:human-review`.
  // (needsMeContext is declared above filteredTasks because filteredTasks
  // depends on it; reorder TDZ fix per build #25916015851 failure.)
  const needsMeCount = useMemo(
    () => countNeedsMe(tasks, needsMeContext),
    [tasks, needsMeContext],
  );

  // HUMAN-REVIEW-NOTIFICATIONS-1: bell + system-notification trigger when a
  // task transitions into needs-me. Bootstrapped on first render so the
  // existing backlog doesn't fire on page load.
  const {
    unseenCount: needsMeUnseen,
    permission: notifPermission,
    requestPermission: requestNotifPermission,
    acknowledge: acknowledgeNeedsMe,
  } = useNeedsMeNotifications(tasks, needsMeContext);

  const toggleNeedsMe = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (needsMeOnly) {
        next.delete('filter');
      } else {
        next.set('filter', 'needs-me');
      }
      return next;
    }, { replace: true });
    // Opening the filter counts as acknowledging the unseen items.
    if (!needsMeOnly) acknowledgeNeedsMe();
  }, [needsMeOnly, setSearchParams, acknowledgeNeedsMe]);

  const handleBellClick = useCallback(async () => {
    // First click on the bell when permission is `default` requests it,
    // then activates the filter. Subsequent clicks just toggle the filter.
    if (notifPermission === 'default') {
      await requestNotifPermission();
    }
    if (!needsMeOnly) {
      // Activate the filter so they see what just got acknowledged.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('filter', 'needs-me');
        return next;
      }, { replace: true });
    }
    acknowledgeNeedsMe();
  }, [notifPermission, requestNotifPermission, needsMeOnly, setSearchParams, acknowledgeNeedsMe]);

  // Kanban sorting: within each column, sort by priority then age
  const kanbanTasks = useMemo(() => {
    return [...filteredTasks].sort(compareTasksForBoard);
  }, [filteredTasks]);

  // Stream view: group by room
  const roomGroups = useMemo(() => {
    const buckets = new Map<RoomKey, TaskItem[]>();
    for (const task of filteredTasks) {
      if (task.tier >= 7) continue;
      const room = taskToRoom(task);
      const list = buckets.get(room) ?? [];
      list.push(task);
      buckets.set(room, list);
    }
    for (const list of buckets.values()) {
      list.sort(compareTasksForBoard);
    }
    return ROOMS
      .map((room) => ({ room, tasks: buckets.get(room.key) ?? [] }))
      .filter((r) => r.tasks.length > 0);
  }, [filteredTasks]);

  // Owner groups for the filter dropdown — humans + agents are surfaced
  // separately so a 4-person team plus a fleet of agents stays readable.
  const ownerGroups = useMemo(() => groupOwners(tasks), [tasks]);
  const meHandle = user?.display_name ?? null;
  const meIsAssigned = useMemo(() => {
    const handle = (meHandle ?? '').trim().toLowerCase();
    const uid = user?.uid;
    if (!handle && !uid) return false;
    return tasks.some((t) => {
      if (uid && t.assigned_user_id === uid) return true;
      if (handle && (t.assigned_to ?? '').trim().toLowerCase() === handle) return true;
      return false;
    });
  }, [tasks, meHandle, user?.uid]);

  // Existing categories (for NewTaskModal)
  const existingCategories = useMemo(() => {
    const set = new Set<string>();
    tasks.forEach((t) => { if (t.category) set.add(t.category); });
    return Array.from(set).sort();
  }, [tasks]);

  // Summary stats
  const stats = useMemo(() => {
    if (!summary) return null;
    const active = summary.by_status?.in_progress ?? 0;
    const review = summary.by_status?.review ?? 0;
    const blocked = summary.by_status?.blocked ?? 0;
    const done = summary.by_status?.done ?? 0;
    return { total: summary.total, active, review, blocked, done, overdue: summary.overdue_count };
  }, [summary]);

  const hasActiveFilters = filterRoom !== 'all' || filterStatus !== 'all' || filterPriority !== 'all' || filterOwner !== 'all' || searchQuery !== '';

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setFilterRoom('all');
    setFilterStatus('all');
    setFilterPriority('all');
    setFilterOwner('all');
  }, []);

  // ── Theme-aware style helpers (driven by CSS vars from ThemeProvider) ───
  // These use Tailwind arbitrary values referencing the CSS custom properties
  // that ThemeProvider applies to :root. When the theme changes, the vars
  // change, and every component that reads them re-paints automatically.
  const bgPrimary = 'bg-[var(--theme-surface-primary)]';
  const bgSecondary = 'bg-[var(--theme-surface-secondary)]';
  const textPrimary = 'text-[var(--theme-text-primary)]';
  const textSecondary = 'text-[var(--theme-text-secondary)]';
  const textMuted = 'text-[var(--theme-text-muted)]';
  const borderColor = 'border-[var(--theme-surface-tertiary)]';
  const inputBg = 'bg-[var(--theme-surface-tertiary)]';
  const inputBorder = 'border-[var(--theme-surface-tertiary)]';
  const hoverBg = isDark ? 'hover:bg-[#1a1d27]' : 'hover:bg-gray-100';

  // ── Render ──────────────────────────────────────────

  return (
    <div className={`min-h-screen ${bgPrimary} ${textPrimary} font-[var(--sp-font-ui)]`}>

      {/* ─── Header ──────────────────────────────────── */}
      <header className={`sticky top-0 z-40 ${bgSecondary} border-b ${borderColor} backdrop-blur-xl`}>
        <div className="px-4 sm:px-6">
          {/* Row 1: Brand + summary stats */}
          <div className="flex items-center justify-between h-14">
            {/* Left: Brand */}
            <div className="flex items-center gap-3">
              <h1 className="text-base font-bold tracking-tight">
                <span className={textSecondary}>Board</span>
              </h1>

              {/* Live indicator */}
              {!loading && (
                <span className="flex items-center gap-1.5 text-[10px] font-mono text-green-500/80">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  LIVE
                </span>
              )}

              {/* Viewing as */}
              {isAuthenticated && user?.display_name && (
                <span className={`text-[10px] font-mono ${readOnly ? 'text-amber-400' : textMuted}`}>
                  {user.display_name}{readOnly ? ' (read-only)' : ''}
                </span>
              )}
            </div>

            {/* Center: Summary stats */}
            {stats && (
              <div className={`hidden md:flex items-center gap-4 text-xs font-mono ${textSecondary}`}>
                <span>{stats.total} tasks</span>
                <span className="text-[#39FF14]">{stats.active} active</span>
                {stats.review > 0 && <span className="text-yellow-400">{stats.review} review</span>}
                {stats.blocked > 0 && <span className="text-red-400">{stats.blocked} blocked</span>}
                <span className="text-green-500">{stats.done} done</span>
                {stats.overdue > 0 && <span className="text-orange-400">{stats.overdue} overdue</span>}
              </div>
            )}

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Notification bell (HUMAN-REVIEW-NOTIFICATIONS-1).
                  Shows a pulsing pip when there are unseen needs-me tasks.
                  First click requests browser-notification permission;
                  subsequent clicks open the needs-me filter and ack all
                  unseen items. Tab-hidden + permission-granted ⇒ system
                  notifications fire when new tasks transition into needs-me. */}
              <button
                onClick={() => { void handleBellClick(); }}
                className={`relative p-2 rounded-lg ${hoverBg} transition-colors ${
                  needsMeUnseen > 0 ? 'text-amber-400' : textSecondary
                }`}
                title={
                  notifPermission === 'unsupported'
                    ? 'Notifications not supported in this browser.'
                    : notifPermission === 'denied'
                      ? `Browser notifications blocked. ${needsMeUnseen} unseen needs-me task${needsMeUnseen === 1 ? '' : 's'}.`
                      : notifPermission === 'default'
                        ? 'Click to enable browser notifications for needs-me tasks.'
                        : needsMeUnseen > 0
                          ? `${needsMeUnseen} unseen needs-me task${needsMeUnseen === 1 ? '' : 's'}. Click to view.`
                          : 'Notifications enabled. No unseen needs-me tasks.'
                }
                aria-label={`Needs-me notifications, ${needsMeUnseen} unseen`}
              >
                {notifPermission === 'denied' ? (
                  <BellOff className="w-4 h-4" />
                ) : (
                  <Bell className="w-4 h-4" />
                )}
                {needsMeUnseen > 0 && (
                  <span
                    className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500 animate-pulse"
                    aria-hidden
                  />
                )}
              </button>

              {/* "What Needs Me" filter toggle (WHAT-NEEDS-ME-VIEW-1).
                  Surfaces tasks parked in workflow:human-review or carrying
                  a meaning-gate tag. Badge count comes from the unfiltered
                  list so it stays accurate alongside other filters. */}
              <button
                onClick={toggleNeedsMe}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  needsMeOnly
                    ? 'bg-[var(--sp-accent-strong)] text-[#0b0d10]'
                    : needsMeCount > 0
                      ? 'bg-amber-500/15 text-amber-400 hover:bg-amber-500/25'
                      : `${hoverBg} ${textMuted}`
                }`}
                title={
                  needsMeOnly
                    ? 'Showing only tasks that need you. Click to clear.'
                    : needsMeCount > 0
                      ? `${needsMeCount} task${needsMeCount === 1 ? '' : 's'} need your input. Click to filter.`
                      : 'Nothing needs your input right now.'
                }
                aria-pressed={needsMeOnly}
              >
                <span>Needs Me</span>
                <span
                  className={`inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full px-1.5 text-[10px] font-bold ${
                    needsMeOnly
                      ? 'bg-[#0b0d10] text-[var(--sp-accent-strong)]'
                      : needsMeCount > 0
                        ? 'bg-amber-500 text-[#0b0d10]'
                        : 'bg-[var(--theme-surface-tertiary)]'
                  }`}
                >
                  {needsMeCount}
                </span>
              </button>

              {/* Share */}
              {!readOnly && (
                <button
                  onClick={() => setShowShareModal(true)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium ${hoverBg} transition-colors ${textSecondary}`}
                >
                  Share
                </button>
              )}

              {/* Theme toggle */}
              <button
                onClick={() => setTheme(isDark ? 'light' as ThemeName : 'default' as ThemeName)}
                className={`p-2 rounded-lg ${hoverBg} transition-colors ${textSecondary}`}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>

              {/* Refresh */}
              <button
                onClick={() => void fetchData()}
                className={`p-2 rounded-lg ${hoverBg} transition-colors ${textSecondary}`}
                title="Refresh"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>

              {/* New task (hidden in read-only) */}
              {!readOnly && (
                <button
                  onClick={() => setShowNewTask(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--sp-accent-strong)] text-[#0b0d10] text-xs font-bold hover:bg-[var(--sp-accent)] transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New Task
                </button>
              )}
            </div>
          </div>

          {/* Row 2: View toggle + Search + Filters */}
          <div className="flex items-center gap-3 pb-3">
            {/* View toggle */}
            <div className={`flex items-center rounded-lg ${inputBg} p-0.5`}>
              {([
                { id: 'kanban' as const, icon: Columns3, label: 'Kanban' },
                { id: 'stream' as const, icon: List, label: 'Stream' },
                { id: 'timeline' as const, icon: Clock, label: 'Timeline' },
              ]).map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  onClick={() => setView(id)}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all
                    ${view === id
                      ? 'bg-[var(--theme-surface-tertiary)] text-[var(--theme-text-primary)] shadow-sm'
                      : `${textMuted}`
                    }
                  `}
                  title={label}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="flex-1 max-w-md relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${textMuted}`} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tasks..."
                className={`
                  w-full pl-9 pr-3 py-1.5 rounded-lg text-xs font-mono
                  ${inputBg} border ${inputBorder} ${textPrimary}
                  placeholder:${textMuted} focus:outline-none focus:ring-1 focus:ring-[var(--sp-accent)]/50
                `}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 ${textMuted} hover:${textSecondary}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Room filter chips */}
            <div className="hidden lg:flex items-center gap-1">
              <button
                onClick={() => setFilterRoom('all')}
                className={`
                  px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors
                  ${filterRoom === 'all'
                    ? 'bg-[var(--theme-surface-tertiary)] text-[var(--theme-text-primary)]'
                    : `${textMuted} ${hoverBg}`
                  }
                `}
              >
                ALL
              </button>
              {ROOMS.map((room) => (
                <button
                  key={room.key}
                  onClick={() => setFilterRoom(room.key)}
                  className={`
                    px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors
                    ${filterRoom === room.key
                      ? 'text-white'
                      : `${textMuted} ${hoverBg}`
                    }
                  `}
                  style={filterRoom === room.key ? {
                    backgroundColor: `${room.color}25`,
                    color: room.color,
                  } : undefined}
                >
                  {room.label}
                </button>
              ))}
            </div>

            {/* Advanced filters toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`
                flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors
                ${showFilters || hasActiveFilters
                  ? 'bg-[var(--sp-accent)]/10 text-[var(--sp-accent)]'
                  : `${textMuted} ${hoverBg}`
                }
              `}
            >
              <Filter className="w-3.5 h-3.5" />
              {hasActiveFilters && (
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--sp-accent)]" />
              )}
            </button>
          </div>

          {/* Row 3: Advanced filter bar (collapsible) */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="overflow-hidden"
              >
                <div className={`flex items-center gap-3 pb-3 border-t ${borderColor} pt-3`}>
                  {/* Status */}
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className={`text-xs font-mono px-2 py-1.5 rounded-lg ${inputBg} border ${inputBorder} ${textPrimary}`}
                  >
                    <option value="all">Active only (default)</option>
                    <option value="all_including_archived">All (incl. done + cancelled)</option>
                    <option value="backlog">Backlog</option>
                    <option value="todo">Todo</option>
                    <option value="in_progress">In progress</option>
                    <option value="blocked">Blocked</option>
                    <option value="review">Review</option>
                    <option value="done">Done</option>
                    <option value="cancelled">Cancelled</option>
                  </select>

                  {/* Priority */}
                  <select
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value)}
                    className={`text-xs font-mono px-2 py-1.5 rounded-lg ${inputBg} border ${inputBorder} ${textPrimary}`}
                  >
                    <option value="all">All priorities</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>

                  {/* Owner — semantic groups (humans/agents/unassigned/@me)
                      then per-handle entries grouped by kind. Tenant filtering
                      is unrelated and stays untouched. */}
                  <select
                    data-testid="board-owner-filter"
                    value={filterOwner}
                    onChange={(e) => setFilterOwner(e.target.value)}
                    className={`text-xs font-mono px-2 py-1.5 rounded-lg ${inputBg} border ${inputBorder} ${textPrimary}`}
                  >
                    <option value="all">All owners</option>
                    {meIsAssigned && <option value="@me">Mine (@me)</option>}
                    {ownerGroups.hasUnassigned && <option value="unassigned">Unassigned</option>}
                    {ownerGroups.humans.length > 0 && <option value="humans">All humans ({ownerGroups.humans.length})</option>}
                    {ownerGroups.agents.length > 0 && <option value="agents">All agents ({ownerGroups.agents.length})</option>}
                    {ownerGroups.humans.length > 0 && (
                      <optgroup label="Humans">
                        {ownerGroups.humans.map((h) => (
                          <option key={`h-${h}`} value={h}>@{h}</option>
                        ))}
                      </optgroup>
                    )}
                    {ownerGroups.agents.length > 0 && (
                      <optgroup label="Agents">
                        {ownerGroups.agents.map((a) => (
                          <option key={`a-${a}`} value={a}>{a}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>

                  {/* Room (mobile — hidden on lg where chips are visible) */}
                  <select
                    value={filterRoom}
                    onChange={(e) => setFilterRoom(e.target.value)}
                    className={`lg:hidden text-xs font-mono px-2 py-1.5 rounded-lg ${inputBg} border ${inputBorder} ${textPrimary}`}
                  >
                    <option value="all">All rooms</option>
                    {ROOMS.map((r) => (
                      <option key={r.key} value={r.key}>{r.label}</option>
                    ))}
                  </select>

                  {/* Task count + Clear */}
                  <span className={`text-[11px] font-mono ${textMuted} ml-auto`}>
                    {filteredTasks.length} of {tasks.length} tasks
                  </span>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-[11px] font-medium text-[var(--sp-accent)] hover:text-[var(--sp-accent)] transition-colors"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* ─── Error banner ────────────────────────────── */}
      {error && (
        <div className="mx-4 mt-3 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
          <span className="text-xs text-red-400 font-mono">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ─── Toast notification ──────────────────────── */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-4 right-4 z-50 px-4 py-2 rounded-lg bg-green-500/10 border border-green-500/20 text-xs font-mono text-green-400"
          >
            {toastMessage}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── No access gate ─────────────────────────── */}
      {/* Overlay and main content are mutually exclusive — they previously
          both rendered when tasks loaded successfully but `noAccess` was
          (incorrectly) true, producing a contradictory "tasks visible +
          access denied" state. */}
      {noAccess && !loading && (
        <div data-testid="board-no-access-overlay" className="flex flex-col items-center justify-center h-64 gap-3">
          <AlertCircle className="w-8 h-8 text-gray-500" />
          <p className="text-sm text-gray-400">You don't have access to this board.</p>
          <p className="text-xs text-gray-600">Ask a board owner for an invite link.</p>
        </div>
      )}

      {/* ─── Loading state ───────────────────────────── */}
      {loading && tasks.length === 0 && (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 text-[var(--sp-accent)] animate-spin" />
        </div>
      )}

      {/* ─── Main content ────────────────────────────── */}
      {!noAccess && (!loading || tasks.length > 0) ? (
        <main data-testid="board-main-content" className="pt-3">
          {/* Kanban View */}
          {view === 'kanban' && (
            <KanbanView
              tasks={kanbanTasks}
              onCardClick={setSelectedTask}
              onStatusChange={handleStatusChange}
              onError={(msg) => setToastMessage(msg)}
            />
          )}

          {/* Stream View */}
          {view === 'stream' && (
            <div className="px-4 sm:px-6 pb-6 space-y-2">
              {roomGroups.map(({ room, tasks: roomTaskList }) => {
                const collapsed = roomCollapsed[room.key] ?? false;
                return (
                  <div key={room.key} className={`rounded-xl bg-[var(--theme-surface-secondary)] border ${borderColor}`}>
                    {/* Room header */}
                    <button
                      onClick={() => setRoomCollapsed((prev) => ({ ...prev, [room.key]: !collapsed }))}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 ${hoverBg} transition-colors rounded-t-xl`}
                    >
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: room.color }} />
                      <span className="text-xs font-bold font-mono uppercase tracking-wider" style={{ color: room.color }}>
                        {room.label}
                      </span>
                      <span className={`text-[10px] font-mono ${textMuted}`}>{roomTaskList.length}</span>
                      <span className={`ml-auto text-xs ${textMuted} transition-transform ${collapsed ? '' : 'rotate-90'}`}>
                        &#9656;
                      </span>
                    </button>
                    {/* Room tasks */}
                    {!collapsed && (
                      <div className="px-2 pb-2 space-y-0.5">
                        {roomTaskList.map((task) => (
                          <TaskRow
                            key={task.id}
                            task={task}
                            onSelect={setSelectedTask}
                            onActivate={handleActivate}
                            onComplete={handleComplete}
                            onOpenPrompt={handleOpenPrompt}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {roomGroups.length === 0 && (
                <div className={`text-center py-16 text-sm ${textMuted}`}>
                  No tasks match the current filters.
                </div>
              )}
            </div>
          )}

          {/* Timeline View */}
          {view === 'timeline' && (
            <div className="px-4 sm:px-6 pb-6 space-y-4">
              <TimelineView tasks={tasks} onSelectTask={setSelectedTask} />
            </div>
          )}
        </main>
      ) : null}

      {/* ─── Detail Panel ────────────────────────────── */}
      <AnimatePresence>
        {selectedTask && (
          <motion.div
            key="detail-panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={`fixed inset-y-0 right-0 w-[480px] max-w-full bg-[var(--theme-surface-primary)] border-l ${borderColor} shadow-2xl z-50`}
          >
            <BoardDetailPanel
              task={selectedTask}
              allTasks={tasks}
              onClose={() => setSelectedTask(null)}
              onUpdate={() => void fetchData()}
              onSelectTask={setSelectedTask}
              readOnly={readOnly}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Backdrop overlay when panel is open ─────── */}
      <AnimatePresence>
        {selectedTask && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedTask(null)}
            className="fixed inset-0 bg-black/40 z-40"
          />
        )}
      </AnimatePresence>

      {/* ─── New Task Modal ──────────────────────────── */}
      <NewTaskModal
        open={showNewTask}
        onClose={() => { setShowNewTask(false); setNewTaskError(null); }}
        onSubmit={handleCreateTask}
        creating={newTaskCreating}
        error={newTaskError}
        existingCategories={existingCategories}
      />

      {/* ─── Share Modal ─────────────────────────────── */}
      <AnimatePresence>
        <BoardShareModal
          open={showShareModal}
          onClose={() => setShowShareModal(false)}
          workspaceId={user?.workspaces?.find((w) => w.thread_type === 'task_board')?.id ?? null}
        />
      </AnimatePresence>
    </div>
  );
}

// Timeline view: strategic milestones (tier >= 7) plus dated tasks in the next
// 60 days. Previously just `<MilestoneTimeline />`, which returns null when no
// tier-7+ tasks exist — leaving the alternate view visibly empty even on a
// board with hundreds of active tasks (walkthrough finding #15).
function TimelineView({
  tasks,
  onSelectTask,
}: {
  tasks: TaskItem[];
  onSelectTask: (task: TaskItem) => void;
}) {
  const milestones = tasks.filter((t) => t.tier >= 7);
  const now = Date.now();
  const horizon = now + 60 * 86400000;

  const upcoming = tasks
    .filter((t) => {
      if (!t.due_date) return false;
      if (t.status === 'done' || t.status === 'cancelled') return false;
      const ts = new Date(t.due_date).getTime();
      return !Number.isNaN(ts) && ts <= horizon;
    })
    .sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime());

  const overdue = upcoming.filter((t) => new Date(t.due_date!).getTime() < now);
  const next = upcoming.filter((t) => new Date(t.due_date!).getTime() >= now);

  // BOARD-DATE-VISIBILITY-1: surface a count of active tasks that have no
  // due_date so the Timeline empty state never reads as "no tasks" when there
  // are plenty of unscheduled rows. The "Undated active tasks" preview below
  // uses created_at / updated_at as metadata (NOT promoted into the dated
  // timeline above).
  const undatedActiveCount = countUndatedActive(tasks);
  const undatedPreview = tasks
    .filter((t) => !t.due_date && t.status !== 'done' && t.status !== 'cancelled')
    .sort((a, b) => {
      // Newest activity first (updated_at if present, else created_at).
      const aTs = new Date(a.updated_at ?? a.created_at).getTime();
      const bTs = new Date(b.updated_at ?? b.created_at).getTime();
      return bTs - aTs;
    })
    .slice(0, 8);

  return (
    <>
      {milestones.length > 0 ? (
        <MilestoneTimeline tasks={tasks} onSelectTask={onSelectTask} />
      ) : (
        <div className="border border-[var(--theme-border-default)] rounded-lg p-4 bg-[var(--theme-surface-secondary)]">
          <div className="text-[10px] font-mono text-[var(--theme-text-muted)] uppercase tracking-wider mb-2">
            Milestones &amp; Goals
          </div>
          <p className="text-xs text-[var(--theme-text-muted)]">
            No strategic milestones filed. Tag a task with tier 7 (milestone), 8 (launch), or 9 (goal) and it'll show
            up on this track.
          </p>
        </div>
      )}

      <div className="border border-[var(--theme-border-default)] rounded-lg p-4 bg-[var(--theme-surface-secondary)]">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[10px] font-mono text-[var(--theme-text-muted)] uppercase tracking-wider">
            Dated tasks · next 60 days
          </div>
          <div className="text-[10px] font-mono text-[var(--theme-text-muted)]">
            {overdue.length > 0 && <span className="text-red-400">{overdue.length} overdue · </span>}
            {next.length} upcoming
          </div>
        </div>
        {upcoming.length === 0 ? (
          <p
            data-testid="timeline-empty-state"
            className="text-xs text-[var(--theme-text-muted)]"
          >
            {timelineDatedEmptyMessage(undatedActiveCount)} Set a{' '}
            <code>due_date</code> on active work to see it scheduled here.
          </p>
        ) : (
          <div className="space-y-1">
            {upcoming.slice(0, 40).map((task) => {
              const due = new Date(task.due_date!);
              const isOverdue = due.getTime() < now;
              const days = Math.ceil((due.getTime() - now) / 86400000);
              const relative =
                isOverdue ? `${Math.abs(days)}d overdue` : days === 0 ? 'today' : `in ${days}d`;
              return (
                <button
                  key={task.id}
                  type="button"
                  onClick={() => onSelectTask(task)}
                  className="w-full flex items-baseline gap-3 text-left px-2 py-1.5 rounded hover:bg-[var(--theme-surface-tertiary)] transition-colors"
                >
                  <span
                    className={`font-mono text-[10px] uppercase tracking-wider w-20 flex-shrink-0 ${
                      isOverdue ? 'text-red-400' : 'text-[var(--theme-text-muted)]'
                    }`}
                  >
                    {due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <span
                    className={`font-mono text-[10px] w-20 flex-shrink-0 ${
                      isOverdue ? 'text-red-400' : 'text-[var(--theme-text-subtle)]'
                    }`}
                  >
                    {relative}
                  </span>
                  <span className="text-xs text-[var(--theme-text-primary)] truncate flex-1">{task.title}</span>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--theme-text-muted)] flex-shrink-0">
                    {task.status.replace('_', ' ')}
                  </span>
                </button>
              );
            })}
            {upcoming.length > 40 && (
              <p className="text-[10px] font-mono text-[var(--theme-text-muted)] pt-2">
                + {upcoming.length - 40} more dated tasks. Switch to Kanban or Stream for the full list.
              </p>
            )}
          </div>
        )}
      </div>

      {/*
        BOARD-DATE-VISIBILITY-1: Undated active tasks panel.
        Strictly separate from the dated timeline above. Sorted by most recent
        activity (updated_at / created_at), labelled as metadata — NOT a
        scheduled date. Hidden when there are no undated active rows.
      */}
      {undatedActiveCount > 0 && (
        <div
          data-testid="timeline-undated-active"
          className="border border-[var(--theme-border-default)] rounded-lg p-4 bg-[var(--theme-surface-secondary)]"
        >
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-[10px] font-mono text-[var(--theme-text-muted)] uppercase tracking-wider">
              Undated active tasks
            </div>
            <div className="text-[10px] font-mono text-[var(--theme-text-muted)]">
              {undatedActiveCount} unscheduled
            </div>
          </div>
          <p className="text-[10px] font-mono text-[var(--theme-text-muted)] mb-2 italic">
            These tasks have no due_date. Dates shown are last activity (updated/created), not scheduled work.
          </p>
          <div className="space-y-1">
            {undatedPreview.map((task) => {
              const updatedIso = task.updated_at;
              const dateIso = updatedIso ?? task.created_at;
              const dateLabel = updatedIso ? 'Updated' : 'Created';
              return (
                <button
                  key={task.id}
                  type="button"
                  onClick={() => onSelectTask(task)}
                  className="w-full flex items-baseline gap-3 text-left px-2 py-1.5 rounded hover:bg-[var(--theme-surface-tertiary)] transition-colors"
                >
                  <span className="font-mono text-[10px] uppercase tracking-wider w-32 flex-shrink-0 text-[var(--theme-text-muted)]">
                    {dateLabel} {formatTaskDate(dateIso)}
                  </span>
                  <span className="text-xs text-[var(--theme-text-primary)] truncate flex-1">{task.title}</span>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--theme-text-muted)] flex-shrink-0">
                    {task.status.replace('_', ' ')}
                  </span>
                </button>
              );
            })}
            {undatedActiveCount > undatedPreview.length && (
              <p className="text-[10px] font-mono text-[var(--theme-text-muted)] pt-2">
                + {undatedActiveCount - undatedPreview.length} more undated active tasks. Switch to Kanban or Stream for the full list.
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
